import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

const CARD = {
  background: '#0b1220',
  border: '1px solid #1e293b',
  borderRadius: 10,
  padding: 18,
  marginBottom: 18,
};
const H2 = { color: '#e2e8f0', fontSize: '1rem', fontWeight: 600, margin: '0 0 12px 0' };
const PILL = (color) => ({
  display: 'inline-block',
  padding: '2px 10px',
  background: color,
  borderRadius: 999,
  color: '#0b1220',
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
});
const BTN = (kind = 'neutral') => {
  const palette = {
    neutral: { bg: '#1e293b', fg: '#e2e8f0', border: '#334155' },
    danger:  { bg: '#7f1d1d', fg: '#fee2e2', border: '#b91c1c' },
    success: { bg: '#065f46', fg: '#d1fae5', border: '#10b981' },
  }[kind];
  return {
    background: palette.bg,
    color: palette.fg,
    border: `1px solid ${palette.border}`,
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: '0.8rem',
    cursor: 'pointer',
    marginRight: 6,
  };
};

function callFn(name, payload) {
  return httpsCallable(getFunctions(), name)(payload).then((r) => r.data);
}

/// Firestore Timestamps survive the httpsCallable wire as `{_seconds, _nanoseconds}`
/// plain objects — NOT Timestamp class instances. Rendering the raw object as a
/// React child throws "Objects are not valid as a React child" and blanks the
/// whole page. Handle every shape we might see (string, real Timestamp, plain).
function formatTs(ts) {
  if (!ts) return '—';
  if (typeof ts === 'string') return ts;
  if (typeof ts.toDate === 'function') return ts.toDate().toLocaleString();
  if (typeof ts._seconds === 'number') return new Date(ts._seconds * 1000).toLocaleString();
  if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000).toLocaleString();
  return String(ts);
}

// ── Enforcement Mode Section ─────────────────────────────────────────────

function EnforcementSection({ serviceName }) {
  const [svc, setSvc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await callFn('adminGetServiceConfig', { serviceName });
      setSvc(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [serviceName]);
  useEffect(() => { reload(); }, [reload]);

  const currentMode = svc?.env?.find(e => e.name === 'APP_CHECK_ENFORCE_MODE')?.value || 'off';

  async function setMode(mode) {
    const warning = mode === 'all'
      ? `Set ${serviceName} APP_CHECK_ENFORCE_MODE = "all"?\n\nThis will REJECT all requests without a valid App Check token. Any client without App Attest configured (or a registered debug token) will start getting 401s.\n\nNew Cloud Run revision deploys immediately.`
      : `Change ${serviceName} APP_CHECK_ENFORCE_MODE from "${currentMode}" → "${mode}"?\n\nNew Cloud Run revision deploys immediately.`;
    if (!window.confirm(warning)) return;
    setBusy(true); setError(null);
    try {
      const res = await callFn('adminSetServiceEnv', {
        serviceName, key: 'APP_CHECK_ENFORCE_MODE', value: mode,
      });
      await reload();
      alert(`✅ ${serviceName} ${mode === 'off' ? 'disabled' : 'set to ' + mode}. New revision: ${res.newRevision || '(deploying)'}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const pillColor = currentMode === 'off' ? '#ef4444' : currentMode === 'all' ? '#10b981' : '#f59e0b';

  return (
    <div style={CARD}>
      <h2 style={H2}>App Check Enforcement ({serviceName})</h2>
      {loading && <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Loading…</div>}
      {error && <div style={{ color: '#fca5a5', fontSize: '0.75rem', marginBottom: 8 }}>{error}</div>}
      {!loading && svc && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Current mode:</span>
            <span style={PILL(pillColor)}>{currentMode}</span>
            <span style={{ color: '#64748b', fontSize: '0.7rem' }}>
              latest revision: <code>{svc.latestReadyRevision || '?'}</code>
            </span>
          </div>
          <div>
            <button style={BTN('neutral')} disabled={busy || currentMode === 'off'} onClick={() => setMode('off')}>off (monitor)</button>
            <button style={BTN('neutral')} disabled={busy || currentMode === 'anonymous'} onClick={() => setMode('anonymous')}>anonymous</button>
            <button style={BTN('success')} disabled={busy || currentMode === 'all'} onClick={() => setMode('all')}>all (enforce)</button>
            <button style={BTN('neutral')} disabled={busy} onClick={reload}>↻ refresh</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Cloud Run Env Viewer Section ─────────────────────────────────────────

function EnvViewerSection() {
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [proxy, api] = await Promise.all([
        callFn('adminGetServiceConfig', { serviceName: 'compute-proxy' }),
        callFn('adminGetServiceConfig', { serviceName: 'web-api' }).catch(() => null),
      ]);
      setServices({ 'compute-proxy': proxy, 'web-api': api });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div style={CARD}>
      <h2 style={H2}>Cloud Run Env (read-only)</h2>
      {loading && <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Loading…</div>}
      {error && <div style={{ color: '#fca5a5', fontSize: '0.75rem', marginBottom: 8 }}>{error}</div>}
      {!loading && Object.entries(services).map(([name, svc]) => (
        <div key={name} style={{ marginBottom: 14 }}>
          <div style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: 6 }}>{name}</div>
          {!svc && <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>(could not load)</div>}
          {svc && (
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#cbd5e1', background: '#0f172a', padding: 10, borderRadius: 6 }}>
              <div style={{ color: '#64748b', marginBottom: 4 }}>
                latest: {svc.latestReadyRevision} · image: {svc.image?.split('/').slice(-1)[0] || '?'}
              </div>
              {svc.env.map(e => (
                <div key={e.name}>
                  <span style={{ color: '#94a3b8' }}>{e.name}</span>=
                  {e.fromSecret
                    ? <span style={{ color: '#a78bfa' }}>{`<secret: ${e.fromSecret}>`}</span>
                    : <span style={{ color: '#86efac' }}>{e.value ?? ''}</span>}
                </div>
              ))}
              {svc.env.length === 0 && <div style={{ color: '#64748b' }}>(no env vars set)</div>}
            </div>
          )}
        </div>
      ))}
      <button style={BTN('neutral')} onClick={reload}>↻ refresh</button>
    </div>
  );
}

// ── Debug Tokens Section ─────────────────────────────────────────────────

function DebugTokensSection() {
  const [tokens, setTokens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [pasteUUID, setPasteUUID] = useState('');

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await callFn('adminListDebugTokens', { appKey: 'ios' });
      setTokens(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  async function create() {
    if (!newDisplayName.trim()) { alert('Display name required.'); return; }
    setBusy(true); setError(null);
    try {
      const res = await callFn('adminCreateDebugToken', {
        appKey: 'ios',
        displayName: newDisplayName.trim(),
        token: pasteUUID.trim() || undefined,
      });
      setNewToken(res);             // shown ONCE — copy now or lose it
      setNewDisplayName('');
      setPasteUUID('');
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(tokenName, displayName) {
    if (!window.confirm(`Revoke debug token "${displayName}"?\n\nAny simulator or device using it will start failing App Check.`)) return;
    setBusy(true); setError(null);
    try {
      await callFn('adminDeleteDebugToken', { tokenName });
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={CARD}>
      <h2 style={H2}>App Check Debug Tokens (iOS)</h2>
      <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: -8, marginBottom: 12 }}>
        For simulators and DEBUG builds. Production devices use App Attest and don't need these.
        <br />
        <strong style={{ color: '#cbd5e1' }}>Usual flow:</strong> run the iOS app in the simulator,
        find the line <code style={{ color: '#86efac' }}>App Check debug token: '…'</code> in the
        Xcode console, copy that UUID, paste it below with a memorable name.
      </p>

      {error && <div style={{ color: '#fca5a5', fontSize: '0.75rem', marginBottom: 8 }}>{error}</div>}

      {newToken && (
        <div style={{ background: '#1e3a8a', border: '1px solid #3b82f6', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          <div style={{ color: '#dbeafe', fontWeight: 600, marginBottom: 6 }}>
            ⚠️ New token created — copy NOW (you can't see the full value again):
          </div>
          <div style={{ fontFamily: 'monospace', background: '#0f172a', padding: 8, borderRadius: 4, color: '#86efac', wordBreak: 'break-all' }}>
            {newToken.token}
          </div>
          <button style={{ ...BTN('neutral'), marginTop: 8 }} onClick={() => setNewToken(null)}>Dismiss</button>
        </div>
      )}

      <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input
          type="text"
          value={newDisplayName}
          onChange={(e) => setNewDisplayName(e.target.value)}
          placeholder='Display name — e.g. "Lawrence iPhone Simulator"'
          style={{ width: 460, padding: '6px 10px', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, fontSize: '0.8rem', fontFamily: 'inherit' }}
        />
        <input
          type="text"
          value={pasteUUID}
          onChange={(e) => setPasteUUID(e.target.value)}
          placeholder='UUID from Xcode console — e.g. DA92D6E0-0F3E-426A-81B5-CEA7AF675382  (leave blank to generate one server-side)'
          style={{ width: 460, padding: '6px 10px', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, fontSize: '0.8rem', fontFamily: 'monospace' }}
        />
        <div>
          <button style={BTN('success')} disabled={busy} onClick={create}>+ Add Token</button>
        </div>
      </div>

      {loading && <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Loading…</div>}
      {!loading && tokens && (
        <div>
          {tokens.tokens.length === 0 && (
            <div style={{ color: '#64748b', fontSize: '0.75rem' }}>(no debug tokens registered)</div>
          )}
          {tokens.tokens.map(t => (
            <div key={t.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1e293b' }}>
              <div>
                <div style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>{t.displayName || '(unnamed)'}</div>
                <div style={{ color: '#64748b', fontSize: '0.7rem', fontFamily: 'monospace' }}>{t.token || ''}</div>
              </div>
              <button style={BTN('danger')} disabled={busy} onClick={() => revoke(t.name, t.displayName)}>Revoke</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Rate Limits Section ──────────────────────────────────────────────────

function RateLimitsSection() {
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await callFn('adminGetRateLimits');
      setLimits(data);
      setEdits({});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  async function save(category) {
    const current = limits[category]?.perMinute;
    const next = Number(edits[category]);
    if (!Number.isInteger(next) || next < 1 || next > 10000) {
      alert('Per-minute must be an integer 1-10000.');
      return;
    }
    if (next === current) return;
    if (!window.confirm(`Set ${category} → ${next}/min (was ${current}/min)?\n\nChange propagates to compute-proxy within 60 seconds.`)) return;
    setBusy(true); setError(null);
    try {
      await callFn('adminSetRateLimit', { category, perMinute: next });
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const categories = limits
    ? Object.entries(limits).filter(([k, v]) => k !== 'updatedAt' && k !== 'updatedBy' && k !== 'seeded' && v && typeof v === 'object')
    : [];

  return (
    <div style={CARD}>
      <h2 style={H2}>Rate Limits (compute-proxy /config/rateLimits)</h2>
      <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: -8, marginBottom: 12 }}>
        Per-uid burst caps. Changes propagate within ~60s (compute-proxy refreshes from Firestore).
      </p>
      {error && <div style={{ color: '#fca5a5', fontSize: '0.75rem', marginBottom: 8 }}>{error}</div>}
      {loading && <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Loading…</div>}
      {!loading && (
        <div>
          {categories.map(([cat, val]) => {
            const current = val.perMinute;
            const draft = edits[cat] ?? String(current);
            const dirty = String(draft) !== String(current);
            return (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #1e293b' }}>
                <div style={{ flex: 1, color: '#e2e8f0', fontFamily: 'monospace', fontSize: '0.8rem' }}>{cat}</div>
                <input
                  type="number"
                  value={draft}
                  onChange={(e) => setEdits({ ...edits, [cat]: e.target.value })}
                  style={{ width: 80, padding: '4px 8px', background: '#0f172a', color: '#e2e8f0', border: `1px solid ${dirty ? '#3b82f6' : '#334155'}`, borderRadius: 4, fontSize: '0.8rem', textAlign: 'right' }}
                />
                <span style={{ color: '#64748b', marginLeft: 8, marginRight: 12, fontSize: '0.75rem' }}>/min</span>
                <button style={dirty ? BTN('success') : BTN('neutral')} disabled={busy || !dirty} onClick={() => save(cat)}>Save</button>
              </div>
            );
          })}
          {categories.length === 0 && (
            <div style={{ color: '#64748b', fontSize: '0.75rem' }}>(no categories — doc may be empty)</div>
          )}
          <div style={{ marginTop: 10, color: '#64748b', fontSize: '0.7rem' }}>
            Last updated: {formatTs(limits?.updatedAt)} by {limits?.updatedBy || '—'}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Audit Log Section ────────────────────────────────────────────────────

function AuditLogSection() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await callFn('adminListAuditLog', { limit: 50 });
      setEntries(data.entries || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div style={CARD}>
      <h2 style={H2}>Audit Log (recent 50)</h2>
      {error && <div style={{ color: '#fca5a5', fontSize: '0.75rem', marginBottom: 8 }}>{error}</div>}
      {loading && <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Loading…</div>}
      {!loading && (
        <div style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
          {entries.length === 0 && <div style={{ color: '#64748b' }}>(no entries yet)</div>}
          {entries.map(e => (
            <div key={e.id} style={{ padding: '4px 0', borderBottom: '1px solid #1e293b' }}>
              <span style={{ color: '#64748b' }}>{e.timestamp?.slice(0, 19).replace('T', ' ')}</span>
              {' '}
              <span style={{ color: '#a78bfa' }}>{e.event}</span>
              {' '}
              <span style={{ color: '#94a3b8' }}>by {e.adminUid?.slice(0, 8)}…</span>
              {' '}
              <span style={{ color: '#cbd5e1' }}>{JSON.stringify(e.payload).slice(0, 200)}</span>
            </div>
          ))}
        </div>
      )}
      <button style={{ ...BTN('neutral'), marginTop: 10 }} onClick={reload}>↻ refresh</button>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function AdminOpsPage() {
  const [uid, setUid] = useState(undefined);
  useEffect(() => {
    return getAuth().onAuthStateChanged(u => setUid(u?.uid || null));
  }, []);

  if (uid === undefined) {
    return <div style={{ padding: 40, color: '#94a3b8', background: '#020617', minHeight: '100vh' }}>Loading…</div>;
  }
  if (!uid) {
    return (
      <div style={{ padding: 40, color: '#e2e8f0', background: '#020617', minHeight: '100vh' }}>
        Sign in required. <Link to="/account" style={{ color: '#3b82f6' }}>Sign in</Link>
      </div>
    );
  }
  if (!ADMIN_UIDS.includes(uid)) {
    return (
      <div style={{ padding: 40, color: '#e2e8f0', background: '#020617', minHeight: '100vh' }}>
        Forbidden: admin access required.
      </div>
    );
  }

  return (
    <div style={{ background: '#020617', minHeight: '100vh', padding: '32px 24px' }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 24 }}>
          <h1 style={{ color: '#f1f5f9', fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>Admin · Ops</h1>
          <Link to="/admin" style={{ color: '#64748b', fontSize: '0.8rem', textDecoration: 'none' }}>← all admin pages</Link>
        </div>

        <EnforcementSection serviceName="compute-proxy" />
        <EnforcementSection serviceName="web-api" />
        <RateLimitsSection />
        <DebugTokensSection />
        <EnvViewerSection />
        <AuditLogSection />
      </div>
    </div>
  );
}
